/*
EXAMPLE USAGE
*
* 
*
Include this script and styles in the parent section where you are using this component: 
<script src="{{ 'accordion.js' | asset_url }}" defer></script>
{{ 'accordion.css' | asset_url | stylesheet_tag }}
*
<content-accordion> 
  <ul> // every accordion item renders as `li`
    # for item in iterable
    {% liquid 
      render 'component-accordion',
      label<string>: [item.label], // toggle button label
      content<string/html>: [item.content], // the content inside the accordion
      id<string>: [item.unique_id], // must be unique per accordion
      default<bool>: true // optional - pass in to have the accordion item open by default
    %}
  </ul>
</content-accordion>
*/

if (!customElements.get('content-accordion')) {
  customElements.define(
    'content-accordion',
    class Accordion extends HTMLElement {
      constructor() {
        super();
      }
    
      connectedCallback() {
        // prep default open accordion height so they close smoothly the first time
        const setDefaultAccordions = () => {
          this.querySelectorAll('[data-accordion-id]').forEach((accordion) => {
            const contentElement = accordion.querySelector('[data-accordion-content]');
            const links = contentElement.querySelectorAll('a');

            if (accordion.classList.contains('active')) {
              contentElement.style.height = `${contentElement.scrollHeight}px`;
              links.forEach(link => link.setAttribute('tabindex', '0'));
            } else {
              links.forEach(link => link.setAttribute('tabindex', '-1'));
            }
          });
        }
        
        // if the children of the accordion are updated, recalculate height vars
        const observer = new MutationObserver(observerCallback.bind(this));
        observer.observe(this, { childList: true, subtree: true });
        
        function observerCallback(mutationList) {
          for (const mutation of mutationList) {
            if (mutation.type === 'childList') {
              setDefaultAccordions();
            }
          }
        }
      
        if (document.readyState === 'complete') {
          setDefaultAccordions();
        } else {
          document.addEventListener('DOMContentLoaded', setDefaultAccordions);
        }
        
        this.addEventListener('click', (e) => {
          const accordionToggle = e.target.closest('[data-accordion-toggle]');
          if (!accordionToggle) return;
          
          const parentElement = accordionToggle.closest('[data-accordion-id]');
          const contentElement = parentElement.querySelector('[data-accordion-content]');
          const toggleElement = parentElement.querySelector('[data-accordion-toggle]');
          const links = contentElement.querySelectorAll('a');
          
          // open/close the current accordion
          if (!parentElement.classList.contains('active')) {
            toggleElement.setAttribute('aria-expanded', true);
            links.forEach(link => link.setAttribute('tabindex', '0'));
          } else {
            contentElement.style.height = '0';
            links.forEach(link => link.setAttribute('tabindex', '-1'));
            // hide elements, update aria attrs for acccessibility when they have finished transitioning out
            setTimeout(() => {
              const accordionContent = e.target.closest('[data-accordion-id]').querySelector('[data-accordion-content]');
              if (!accordionContent) return;
              
              accordionContent.closest('[data-accordion-id]')
                ?.querySelector('[data-accordion-toggle]')
                ?.setAttribute('aria-expanded', false);
            }, 1);
          }
          
          setTimeout(() => {
            // open the current accordion
            if (!parentElement.classList.contains('active')) {
              contentElement.style.height = `${contentElement.scrollHeight}px`;
              links.forEach(link => link.setAttribute('tabindex', '0'));
            } else {
              contentElement.style.height = '0';
              links.forEach(link => link.setAttribute('tabindex', '-1'));
            }
            parentElement.classList.toggle('active');

            // close all the other accordions that might be open
            [...this.querySelectorAll('[data-accordion-id].active')]
              .filter((node) => !node.isSameNode(parentElement))
              .forEach((inactiveParentElement) => {
                const inactiveAccordionContent = inactiveParentElement.querySelector('[data-accordion-content]');
                const inactiveLinks = inactiveAccordionContent?.querySelectorAll('a');
                
                inactiveParentElement.classList.remove('active');
                inactiveAccordionContent.style.height = 0;
                inactiveParentElement.querySelector('[data-accordion-toggle]')
                  .setAttribute('aria-expanded', false);
                inactiveLinks.forEach(link => link.setAttribute('tabindex', '-1'));
              });
          }, 1);
        })
      }
    }
  );
}

